import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactElement,
  type RefObject,
} from 'react';
import { Merge, X } from 'lucide-react';

import { Button, Dialog, EmptyState, IconButton, Notice, TextField } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface CardMergeCandidate {
  readonly id: string;
  readonly title: string;
  readonly listTitle: string;
}

export interface CardMergeDialogProps {
  readonly open: boolean;
  /**
   * Focused on close instead of the element that opened the dialog, for an
   * opener that no longer exists by then: the card back opens this from an
   * item of its "⋯" Actions menu, and hands focus back to the menu trigger.
   */
  readonly returnFocus?: RefObject<HTMLElement | null> | undefined;
  readonly currentCardTitle: string;
  readonly candidates: readonly CardMergeCandidate[];
  readonly busy?: boolean | undefined;
  readonly error?: string | null | undefined;
  readonly onCancel: () => void;
  readonly onMerge: (sourceCard: CardMergeCandidate) => void;
}

export function CardMergeDialog({
  open,
  currentCardTitle,
  candidates,
  busy,
  error,
  onCancel,
  onMerge,
  returnFocus,
}: CardMergeDialogProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const [selectedCardId, setSelectedCardId] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelectedCardId(candidates[0]?.id ?? '');
    setQuery('');
  }, [candidates, open]);

  const filteredCandidates = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (normalizedQuery === '') return candidates;
    return candidates.filter((candidate) =>
      `${candidate.title} ${candidate.listTitle}`
        .toLocaleLowerCase()
        .includes(normalizedQuery),
    );
  }, [candidates, query]);

  if (!open) return null;

  const selectedCard =
    candidates.find((candidate) => candidate.id === selectedCardId) ?? null;
  /**
   * ⚠ FE-80.1 — A GUARD THAT COULD NOT FAIL, and the routing slice is what
   * exposed it. As shipped, the second arm tested `selectedCard === null`,
   * which with a non-empty `candidates` is TRUE only for the one commit before
   * `useEffect` sets `selectedCardId` to `candidates[0].id` — a state React
   * flushes before any assertion and before any paint. So
   * `cardBack.merge.noSelectionReason` had no reader, and routing it as written
   * would have shipped a dead string to translators.
   *
   * The condition that IS reachable is the one the string already describes:
   * the search box filters the selected card off screen, the radio group shows
   * nothing checked, and the shipped dialog left submit ENABLED for a card the
   * reader can no longer see — merging by a selection made before the search.
   * Testing visibility rather than nullity closes that and gives the value its
   * reader in the same line.
   */
  const selectedCardVisible =
    selectedCard !== null &&
    filteredCandidates.some((candidate) => candidate.id === selectedCard.id);
  const disabledReason =
    candidates.length === 0
      ? t('cardBack.merge.noCandidatesReason')
      : !selectedCardVisible
        ? t('cardBack.merge.noSelectionReason')
        : undefined;
  const submitDisabled = disabledReason !== undefined;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy === true) return;
    if (selectedCard === null) return;
    // The disabled submit button is not the only way into this handler —
    // pressing Enter in the search field submits the form implicitly — so the
    // off-screen-selection guard is repeated here rather than left to the
    // button's `disabled`.
    if (!selectedCardVisible) return;
    onMerge(selectedCard);
  }

  return (
    <Dialog
      {...(returnFocus ? { returnFocus } : {})}
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      size="md"
      title={t('cardBack.merge.title', { card: currentCardTitle })}
      description={t('cardBack.merge.description')}
      closeControl={
        <IconButton label={t('cardBack.merge.close')} size="sm">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      footer={
        <>
          <Button size="sm" variant="quiet" onClick={onCancel}>
            {t('cardBack.merge.cancel')}
          </Button>
          <Button
            type="submit"
            form="card-back-merge-form"
            size="sm"
            variant="primary"
            busy={busy ?? false}
            busyLabel={t('cardBack.merge.busy')}
            disabled={submitDisabled}
            disabledReason={disabledReason}
            /*
             * ⚠ The unselected arm reads the SAME key as the visible label
             * below, not a second key carrying the same English. WCAG 2.5.3
             * requires the two to coincide, and one key is the only spelling of
             * that a translator cannot break.
             */
            aria-label={
              selectedCard === null
                ? t('cardBack.merge.submit')
                : t('cardBack.merge.submitLabel', {
                    card: currentCardTitle,
                    source: selectedCard.title,
                  })
            }
          >
            {t('cardBack.merge.submit')}
          </Button>
        </>
      }
    >
      <form id="card-back-merge-form" className="flex flex-col gap-4" onSubmit={submit}>
        <Notice
          tone="warn"
          title={t('cardBack.merge.consequencesTitle')}
          body={t('cardBack.merge.consequencesBody', { card: currentCardTitle })}
        />

        {error ? (
          <Notice tone="bad" title={t('cardBack.merge.failedTitle')} body={error} />
        ) : null}

        {candidates.length === 0 ? (
          <EmptyState
            tone="neutral"
            size="inline"
            title={t('cardBack.merge.emptyTitle')}
            body={t('cardBack.merge.emptyBody')}
          />
        ) : (
          <>
            <TextField
              label={t('cardBack.merge.searchLabel')}
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              autoComplete="off"
            />
            <fieldset className="flex flex-col gap-2">
              <legend className="text-sm font-semibold text-ink">
                {t('cardBack.merge.legend')}
              </legend>
              {filteredCandidates.length === 0 ? (
                <p className="text-sm text-ink-muted">{t('cardBack.merge.noMatches')}</p>
              ) : (
                <div className="max-h-56 overflow-y-auto rounded-card border border-border-strong bg-surface-raised p-1">
                  {filteredCandidates.map((candidate) => (
                    <label
                      key={candidate.id}
                      className="flex cursor-pointer items-start gap-2 rounded-card px-2 py-2 text-sm text-ink hover:bg-surface"
                    >
                      <input
                        type="radio"
                        name="card-back-merge-source"
                        value={candidate.id}
                        checked={selectedCardId === candidate.id}
                        onChange={() => setSelectedCardId(candidate.id)}
                        className="mt-1"
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-medium">
                          {candidate.title}
                        </span>
                        <span className="block truncate text-xs text-ink-muted">
                          {candidate.listTitle}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </fieldset>
          </>
        )}

        <p className="flex items-center gap-2 text-xs text-ink-muted">
          <Merge aria-hidden="true" className="size-3.5" />
          {t('cardBack.merge.footnote')}
        </p>
      </form>
    </Dialog>
  );
}
